import { Stack, Switch, FormControlLabel, CircularProgress } from "@mui/material";
import { useMemo } from "react";
import CreateIcon from "@mui/icons-material/Create";
import { Button, useDataProvider, useNotify } from "react-admin";
import CustomDialog from "shared/components/dialogs/CustomDialog";
import { useGroupCertificationModalContext } from "../hooks/useGroupCertifModal";
import { OptimizerResourcesEnum } from "shared/enums/resourceMappings";
import { useState } from "react";
import { getDataproviderPydanticErrorMessage } from "shared/utils/errorHandlers";
import { SiteContryEnum } from "shared/enums/globalEnums";
import { OptimizerCertificationStatusEnum } from "shared/enums/opti_enums";
import { useGroupCertificationCreate } from "entities/optimizer/groups_certifications/create/hooks/useGroupCertificationCreate";
import { GroupCertificationsCreateValues } from "entities/optimizer/groups_certifications/create/types";
import { OptimizerFinlandGroupCertificationTypeEnum } from "shared/enums/opti_enums";

const CreateAssociatedGroupCertificationModal = () => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const { isModalOpen, handleModalClose, isModalLoading, setIsModalLoading, resolveModal, siteId, startDate } =
    useGroupCertificationModalContext();
  const {
    certificationNameById,
    certificationIdByName,
    getCertificationPowerFromId,
    transformFormValues,
    handleSuccess,
    handleError,
  } = useGroupCertificationCreate();
  const [certifToCreate, setCertifToCreate] = useState<Record<OptimizerFinlandGroupCertificationTypeEnum, boolean>>({
    [OptimizerFinlandGroupCertificationTypeEnum.FCR_N]: true,
    [OptimizerFinlandGroupCertificationTypeEnum.FCR_D_SYMMETRICAL]: true,
    [OptimizerFinlandGroupCertificationTypeEnum.AFRR_ASYMMERICAL_FINLAND]: true,
  });

  const toggleCertifToCreate = (key: OptimizerFinlandGroupCertificationTypeEnum) => {
    setCertifToCreate((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const certifToCreateValues = useMemo(() => {
    if (!certificationIdByName) {
      return [];
    }
    return Object.entries(certifToCreate)
      .filter(([, value]) => value)
      .map(([certifName]) => {
        const certifId = certificationIdByName[certifName];
        return {
          country: SiteContryEnum.Finland,
          certification_id: certifId,
          certified_power: getCertificationPowerFromId(certifId) ?? 0,
          site_id: siteId?.toString(),
          status: OptimizerCertificationStatusEnum.TRIAL,
          start_date: startDate ?? new Date(),
        };
      });
  }, [siteId, startDate, certifToCreate, certificationIdByName, getCertificationPowerFromId]);

  const createFinlandSitesGroupCertifications = async () => {
    setIsModalLoading(true);
    await Promise.all(
      certifToCreateValues.map(async (values) => {
        const market = certificationNameById[values.certification_id];
        await dataProvider
          .create(OptimizerResourcesEnum.OptiGroupsCertifications, {
            data: transformFormValues(values as GroupCertificationsCreateValues),
          })
          .then(async (result) => {
            await handleSuccess(result?.data, values.certified_power, market);
            notify(`Associated group certification of type ${market} created successfully`, {
              type: "success",
            });
          })
          .catch((error: any) => {
            handleError(error);
            notify(getDataproviderPydanticErrorMessage(error), { type: "error" });
          });
      }),
    );
    setIsModalLoading(false);
    resolveModal(true);
  };

  if (!certificationIdByName) {
    return (
      <CustomDialog open={isModalOpen} onClose={handleModalClose} title="Create associated Group Certifications">
        <CircularProgress />
      </CustomDialog>
    );
  }

  return (
    <CustomDialog
      open={isModalOpen}
      onClose={handleModalClose}
      title="Create associated Group Certifications"
      actionComponentContent={
        <Stack direction="row" spacing={2} width={"100%"} justifyContent={"end"}>
          <Button onClick={handleModalClose} variant="outlined" label="No" size="large" disabled={isModalLoading} />
          <Button
            onClick={createFinlandSitesGroupCertifications}
            variant="contained"
            label="Yes"
            size="large"
            startIcon={isModalLoading ? <CircularProgress size={15} color="inherit" /> : <CreateIcon />}
            disabled={isModalLoading}
          />
        </Stack>
      }
    >
      <Stack direction="column">
        <FormControlLabel
          control={
            <Switch
              checked={certifToCreate[OptimizerFinlandGroupCertificationTypeEnum.FCR_N]}
              onChange={() => {
                toggleCertifToCreate(OptimizerFinlandGroupCertificationTypeEnum.FCR_N);
              }}
            />
          }
          label="Create group certification of type FCR-N"
        />
        <FormControlLabel
          control={
            <Switch
              checked={certifToCreate[OptimizerFinlandGroupCertificationTypeEnum.FCR_D_SYMMETRICAL]}
              onChange={() => {
                toggleCertifToCreate(OptimizerFinlandGroupCertificationTypeEnum.FCR_D_SYMMETRICAL);
              }}
            />
          }
          label="Create group certification of type FCR-D symmetrical"
        />
        <FormControlLabel
          control={
            <Switch
              checked={certifToCreate[OptimizerFinlandGroupCertificationTypeEnum.AFRR_ASYMMERICAL_FINLAND]}
              onChange={() => {
                toggleCertifToCreate(OptimizerFinlandGroupCertificationTypeEnum.AFRR_ASYMMERICAL_FINLAND);
              }}
            />
          }
          label="Create group certification of type AFRR Asymmetrical for Finland"
        />
      </Stack>
    </CustomDialog>
  );
};

export default CreateAssociatedGroupCertificationModal;
